import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { hydrate } from "@/lib";
import type { QuickPoll } from "@/types";
import { fetchQuickPoll, saveQuickPoll } from "./thunk";

export interface QuickPollState {
  quickPoll: Partial<QuickPoll>;
  status: "idle" | "fetching" | "saving";
  answer: { optionId: null | string | number };
  error: string | null;
  viewResult: boolean;
  taken: boolean;
  showThanks: boolean;
  editingResponse: boolean;
}

const initialState: QuickPollState = {
  quickPoll: {},
  status: "idle",
  error: null,
  viewResult: false,
  taken: false,
  showThanks: false,
  editingResponse: false,
  answer: { optionId: null }
};

export const quickPollSlice = createSlice({
  name: "quickPoll",
  initialState,
  reducers: {
    toggleResult: (state) => {
      state.answer["optionId"] = null;
      state.viewResult = !state.viewResult;
    },
    showForm: (state) => {
      state.viewResult = false;
      state.editingResponse = true;
      state.showThanks = false;
    },
    showResults: (state) => {
      state.viewResult = true;
      state.editingResponse = false;
    },
    markTaken: (
      state,
      action: PayloadAction<{ optionId?: string | number | null }>
    ) => {
      state.taken = true;
      state.viewResult = true;
      state.editingResponse = false;
      state.quickPoll.takenByCurrentUser = true;
      if (action.payload.optionId != null) {
        state.answer.optionId = action.payload.optionId;
      }
    },
    clearTaken: (state) => {
      state.taken = false;
      state.viewResult = false;
      state.showThanks = false;
      state.editingResponse = false;
      state.answer.optionId = null;
      state.quickPoll.takenByCurrentUser = false;
      state.quickPoll.userOptionId = null;
      state.quickPoll.userResponseId = null;
    },
    updateQuickPoll: (state, action) => {
      state.error = null;
      state.answer["optionId"] = action.payload;
    },
    resetQuickPoll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => {
        const incoming = (action.payload[quickPollSlice.name] ||
          {}) as Partial<QuickPollState>;
        return {
          ...state,
          ...incoming
        };
      })
      .addCase(fetchQuickPoll.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchQuickPoll.fulfilled, (state, action) => {
        state.status = "idle";
        state.quickPoll = action.payload;

        if (action.payload.takenByCurrentUser) {
          state.taken = true;
          state.viewResult = true;
          state.editingResponse = false;

          if (action.payload.userOptionId != null) {
            state.answer.optionId = action.payload.userOptionId;
          }
        } else if (!state.showThanks) {
          state.taken = false;
          state.viewResult = false;
          state.editingResponse = false;
        }
      })
      .addCase(fetchQuickPoll.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(saveQuickPoll.pending, (state) => {
        state.status = "saving";
      })
      .addCase(saveQuickPoll.fulfilled, (state, action) => {
        state.status = "idle";
        state.taken = true;
        state.viewResult = true;
        state.showThanks = true;
        state.editingResponse = false;

        const { optionId, userResponseId } = action.payload || {};
        if (optionId != null) {
          state.answer.optionId = optionId;
          state.quickPoll.userOptionId = String(optionId);
          state.quickPoll.takenByCurrentUser = true;
        }
        if (userResponseId != null) {
          state.quickPoll.userResponseId = String(userResponseId);
        }
      })
      .addCase(saveQuickPoll.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      });
  }
});

export const {
  toggleResult,
  showForm,
  showResults,
  markTaken,
  clearTaken,
  updateQuickPoll,
  resetQuickPoll
} = quickPollSlice.actions;
